/**
 * Oak & Sprout — personalisation (engraving / name tape) toggle.
 *
 * Each product template carries two native line_item_property blocks:
 *   - a checkbox block  (the opt-in toggle — its label is free wording, never a property key)
 *   - a text block      (the engraving field — its label IS the permanent property key)
 * plus, on bundles and vehicles, a guidance block with data-block-id "os_engraving_guidance".
 *
 * Behaviour (see .claude/PERSONALISATION_BUILD.md §A3):
 *   toggle off (default)          -> field hidden + disabled, guidance hidden, nothing submits
 *   toggle on, field empty        -> field visible, ENABLED and focusable; formdata strips the blank value
 *   toggle on, field non-empty    -> only properties[<field label>] submits
 *   the toggle itself             -> never submits: its name attribute is stripped at init,
 *                                    and its recorded key is deleted in the formdata hook as a backstop
 *
 * Emptiness is handled ONLY at submit time (formdata event) — never by disabling the
 * input on empty, which would make the field dead the moment it is revealed.
 *
 * Copy applied from window.osEngraving (snippets/os-personalisation-config.liquid):
 *   - placeholder: the grey hint inside the field. The theme hard-codes
 *     placeholder="{{ label }}" (snippets/input.liquid:65) and only accepts a custom one
 *     when show_label_as_block is set (:44), which the line_item_property render never
 *     passes — so it is set here instead. The floating <label> (input.liquid:73) is
 *     untouched and still reads the property key ("Engraving" / "Name tape").
 * It never touches the text field's label — that is the permanent property key (§A2).
 *
 * Known limitation (deliberate, PERSONALISATION_BUILD.md §A6): content injected after
 * init — e.g. the quick-buy modal's <product-rerender> full replaceWith, reachable only
 * for multi-variant products (today: the Gift Card, whose blocks are disabled) — is not
 * re-initialised. Whoever enables the Gift Card blocks or adds a second variant to any
 * product must re-run init on modal open.
 */
(function () {
  'use strict';

  var BLOCK_SELECTOR = '.product-info__block-item[data-block-type="line-item-property"]';
  var GUIDANCE_SELECTOR = '.product-info__block-item[data-block-id="os_engraving_guidance"]';

  function containerFor(el) {
    return el.closest('.product-info') || el.closest('[id^="shopify-section"]') || document;
  }

  function initPair(toggleBlock) {
    if (toggleBlock.dataset.osPersonalisationInit) return;

    var checkbox = toggleBlock.querySelector('input[type="checkbox"]');
    if (!checkbox) return;

    var scope = containerFor(toggleBlock);
    var fieldBlock = null;
    var fieldInput = null;
    scope.querySelectorAll(BLOCK_SELECTOR).forEach(function (block) {
      if (fieldInput || block === toggleBlock) return;
      var input = block.querySelector('input[type="text"], textarea');
      if (input && input.form && input.form === checkbox.form) {
        fieldBlock = block;
        fieldInput = input;
      }
    });
    if (!fieldInput) return;

    var guidanceBlock = scope.querySelector(GUIDANCE_SELECTOR);
    var form = checkbox.form;

    // Per-product copy (see the header note). Presentation only.
    var config = window.osEngraving || {};
    if (config.placeholder) fieldInput.setAttribute('placeholder', config.placeholder);

    // Small right-aligned note above the box. Inserted inside fieldBlock so it reveals
    // and hides with the field automatically — sync() needs no knowledge of it. Engraving
    // is reproduced exactly as typed, so the customer needs to know capitals are kept.
    if (!fieldBlock.querySelector('.os-engraving-note')) {
      var note = document.createElement('span');
      note.className = 'os-engraving-note text-sm text-subdued text-end';
      note.textContent = 'Case sensitive text';
      fieldBlock.insertBefore(note, fieldBlock.firstChild);
    }

    // The toggle must never reach the order as a property (§A3.2). Strip its name so no
    // serializer — the theme's FormData path or Shopify's accelerated-checkout script —
    // can pick it up; keep the original key for the formdata backstop below.
    var toggleKey = checkbox.getAttribute('name');
    checkbox.removeAttribute('name');
    toggleBlock.dataset.osPersonalisationInit = 'true';

    function sync(focusField) {
      var on = checkbox.checked;
      fieldBlock.hidden = !on;
      // Explicit 'block' (not '') when revealed: the os-personalisation.css
      // pre-hide rule sets display:none at stylesheet level, and only an
      // inline value outranks it.
      fieldBlock.style.display = on ? 'block' : 'none';
      // Disabled while hidden keeps the empty field out of EVERY submit path; it is
      // re-enabled the moment it is revealed and never disabled on emptiness (§A3.3).
      fieldInput.disabled = !on;
      if (guidanceBlock) {
        guidanceBlock.hidden = !on;
        guidanceBlock.style.display = on ? 'block' : 'none';
      }
      if (on && focusField) fieldInput.focus();
    }

    checkbox.addEventListener('change', function () {
      sync(true);
    });

    // Browser back/forward cache can restore a checked toggle and typed value — resync
    // rather than wiping what the customer typed.
    window.addEventListener('pageshow', function () {
      sync(false);
    });

    if (form && !form.dataset.osPersonalisationFormdata) {
      form.dataset.osPersonalisationFormdata = 'true';
      form.addEventListener('formdata', function (event) {
        var fd = event.formData;
        if (toggleKey) fd.delete(toggleKey);
        var key = fieldInput.getAttribute('name');
        if (key) {
          var value = fd.get(key);
          if (typeof value === 'string' && value.trim() === '') fd.delete(key);
        }
      });
    }

    sync(false);
  }

  function initAll(root) {
    (root || document).querySelectorAll(BLOCK_SELECTOR).forEach(function (block) {
      if (block.querySelector('input[type="checkbox"]')) initPair(block);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // Theme editor re-renders sections after edits; re-init so a preview in the
  // customiser behaves like the live page.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
